import { useEffect, useId, useMemo, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";

import { Button } from "@/ui/button";
import { HintNote } from "@/components/HintTip";
import { cn } from "@/lib/utils";
import { ProductLinkCard } from "@/features/affiliates/components/MyAffiliateLink";
import {
  MyAffiliateProductList,
  type ProductChoice,
} from "@/features/affiliates/components/MyAffiliateProductList";
import { requestMyAffiliateLink } from "@/features/affiliates/my-code-api";
import {
  catalogLinkFor,
  pickableProducts,
  productReferralLinks,
  type ReferralLink,
} from "@/features/affiliates/referral-link";
import { useBrandLabel, useProductCatalog, useSellingBrands } from "@/features/products/catalogApi";
import { useLookbookConcepts } from "@/features/lookbook/api";

/** Sổ về chậm hơn thế này thì thôi chờ, mời thử lại (AFF thường gửi trong vài giây). */
const POLL_EVERY_MS = 3_000;
const GIVE_UP_AFTER_MS = 60_000;

type Phase =
  | { state: "idle" }
  | { state: "waiting"; label: string }
  | { state: "failed"; message: string };

/**
 * Ô «Chọn sản phẩm» trong hộp "Mã AFF của tôi" (bản 2 — CEO chốt 1a · 2a · 3a,
 * 30/10/2026; thay khối hiện mọi link sản phẩm). Nguồn = «Sản phẩm đang đẩy»
 * (link Marketing trong sổ AFF) xếp đầu, rồi bảng giá GNPF: gói đang bán, hiện
 * trên trang đặt lịch, đủ bốn thương hiệu. Chọn xong mới hiện link của món đó;
 * món chưa có link thì xin AFF tạo (máy chủ xếp lệnh qua Center) và đọc lại sổ
 * vài giây một lần cho tới khi link về.
 */
export function MyAffiliateProductPicker({
  links,
  reload,
}: {
  links: ReferralLink[];
  reload: () => Promise<void>;
}) {
  const source = usePickerSource(links);
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<ProductChoice | null>(null);
  const [conceptId, setConceptId] = useState("");
  const listId = useId();

  const conceptName =
    choice?.kind === "catalog" ? choice.concepts.find((c) => c.id === conceptId)?.name : undefined;
  const label = choice ? (conceptName ? `${choice.name} · ${conceptName}` : choice.name) : "";
  const current =
    choice?.kind === "pushed"
      ? source.pushed.find((l) => l.productId === choice.productId)
      : choice?.kind === "catalog"
        ? catalogLinkFor(links, choice.packageCode, conceptId)
        : undefined;
  const { phase, ask, reset } = useLinkRequest(links, reload, current !== undefined);

  const pick = (next: ProductChoice) => {
    setChoice(next);
    setConceptId("");
    setOpen(false);
    if (next.kind === "catalog") void ask(next.packageCode, "", next.name);
    else reset();
  };

  const pickConcept = (id: string) => {
    setConceptId(id);
    if (choice?.kind !== "catalog") return;
    const name = choice.concepts.find((c) => c.id === id)?.name;
    void ask(choice.packageCode, id, name ? `${choice.name} · ${name}` : choice.name);
  };

  return (
    <section
      aria-label="Link theo sản phẩm"
      className="rounded-md border border-border bg-surface px-4 py-3"
    >
      <div className="text-sm text-muted-foreground">Link theo sản phẩm</div>
      <HintNote className="mt-1">
        Chọn món muốn giới thiệu: khách bấm link là trang đặt lịch mở sẵn món đó, đơn vẫn ghi mã của
        bạn.
      </HintNote>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "mt-2 flex min-h-11 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm",
          !choice && "text-muted-foreground",
        )}
      >
        <span className="min-w-0 truncate">
          {choice ? choice.name : "Chọn sản phẩm để lấy link"}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>
      {open ? (
        <MyAffiliateProductList id={listId} {...source} selectedKey={choice?.key} onPick={pick} />
      ) : null}
      {choice?.kind === "catalog" && choice.concepts.length > 0 ? (
        <select
          aria-label="Concept"
          value={conceptId}
          onChange={(e) => pickConcept(e.target.value)}
          className="mt-2 min-h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">Không chọn concept (khách tự chọn khi đặt)</option>
          {choice.concepts.map((c) => (
            <option key={c.id} value={c.id}>
              Concept: {c.name}
            </option>
          ))}
        </select>
      ) : null}
      {choice ? (
        <PickedLink
          link={current}
          label={label}
          phase={phase}
          onRetry={
            choice.kind === "catalog"
              ? () => void ask(choice.packageCode, conceptId, label)
              : undefined
          }
        />
      ) : null}
    </section>
  );
}

/** Nguồn của danh sách: sản phẩm đang đẩy (sổ AFF) + bảng giá + tên concept. */
function usePickerSource(links: ReferralLink[]) {
  const brandLabel = useBrandLabel();
  const catalog = useProductCatalog();
  const selling = useSellingBrands();
  const concepts = useLookbookConcepts();
  const pushed = useMemo(() => productReferralLinks(links), [links]);
  const conceptNames = useMemo(() => {
    const out = new Map<string, string>();
    for (const c of concepts.data ?? []) if (c.isActive) out.set(c.id, c.name);
    return out;
  }, [concepts.data]);
  // Bảng giá tĩnh dự phòng (API lỗi) có thể đã cũ — không mời xin link theo nó.
  const broken = catalog.isFallback && !catalog.isLoading;
  const products = useMemo(
    () => (broken ? [] : pickableProducts(catalog.products, selling)),
    [catalog.products, broken, selling],
  );
  return { pushed, products, conceptNames, brandLabel, loading: catalog.isLoading, broken };
}

/**
 * Xin link cho một món rồi chờ sổ về: đọc lại sổ vài giây một lần, quá một
 * phút thì thôi và mời thử lại. Món đã có link thì không xin.
 */
function useLinkRequest(links: ReferralLink[], reload: () => Promise<void>, hasLink: boolean) {
  const [phase, setPhase] = useState<Phase>({ state: "idle" });

  const ask = async (packageCode: string, concept: string, name: string) => {
    if (catalogLinkFor(links, packageCode, concept)) {
      setPhase({ state: "idle" });
      return;
    }
    setPhase({ state: "waiting", label: name });
    try {
      await requestMyAffiliateLink(concept ? { packageCode, conceptId: concept } : { packageCode });
    } catch (e) {
      setPhase({
        state: "failed",
        message: e instanceof Error ? e.message : "Chưa xin được link cho sản phẩm này",
      });
    }
  };

  const waiting = phase.state === "waiting" && !hasLink;
  useEffect(() => {
    if (!waiting) return;
    const poll = window.setInterval(() => void reload().catch(() => undefined), POLL_EVERY_MS);
    const stop = window.setTimeout(
      () => setPhase({ state: "failed", message: "Chưa nhận được link — thử lại sau ít phút." }),
      GIVE_UP_AFTER_MS,
    );
    return () => {
      window.clearInterval(poll);
      window.clearTimeout(stop);
    };
  }, [waiting, reload]);

  return { phase, ask, reset: () => setPhase({ state: "idle" }) };
}

function PickedLink({
  link,
  label,
  phase,
  onRetry,
}: {
  link: ReferralLink | undefined;
  label: string;
  phase: Phase;
  onRetry: (() => void) | undefined;
}) {
  if (link) return <ProductLinkCard link={link} name={label} />;
  if (phase.state === "waiting") {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-md bg-surface-2 px-3 py-2.5 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
        <span>Đang tạo link cho «{phase.label}»… thường vài giây.</span>
      </div>
    );
  }
  if (phase.state === "failed") {
    return (
      <div
        role="alert"
        className="mt-3 rounded-md border border-border px-3 py-2.5 text-sm text-destructive"
      >
        <p>{phase.message}</p>
        {onRetry ? (
          <Button type="button" variant="outline" size="sm" className="mt-2" onClick={onRetry}>
            Thử lại
          </Button>
        ) : null}
      </div>
    );
  }
  return null;
}
